/**
 * RunwayDiagram — SVG-based visual representation of airport runways.
 *
 * Renders each runway as an oriented strip based on its magnetic heading,
 * with labels at each end showing designator and key details.
 */
import type { Runway } from '../types/airport';

interface Props {
  runways: Runway[];
  /** Width of the SVG viewport in pixels */
  width?: number;
  /** Height of the SVG viewport in pixels */
  height?: number;
  /** Called when a runway end is clicked */
  onRunwayClick?: (runway: Runway, endId: string) => void;
}

const SURFACE_COLORS: Record<string, string> = {
  ASPH: '#2A2A2A',
  CONC: '#4A4A4A',
  ASPH_CONC: '#353535',
  TURF: '#4A7C3B',
  GRVL: '#8B7355',
  WATER: '#4A7BC8',
  DIRT: '#8B6F47',
  SAND: '#C2B280',
  UNKN: '#555555',
};

function getSurfaceColor(surface: string): string {
  const key = surface.toUpperCase().replace(/[^A-Z_]/g, '');
  return SURFACE_COLORS[key] ?? SURFACE_COLORS.UNKN;
}

export function RunwayDiagram({
  runways,
  width = 400,
  height = 400,
  onRunwayClick,
}: Props) {
  if (!runways || runways.length === 0) {
    return (
      <div className="runway-diagram-empty">
        <p>No runway data available</p>
      </div>
    );
  }

  const centerX = width / 2;
  const centerY = height / 2;

  // Find longest runway to scale others relative to it
  const maxLength = Math.max(...runways.map((r) => r.length || 1));
  const scale = Math.min(width, height) * 0.4;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="runway-diagram"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Airport runway diagram"
    >
      {/* Compass reference */}
      <circle
        cx={centerX}
        cy={centerY}
        r={scale * 1.1}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth="1"
        strokeDasharray="4,4"
      />
      <text
        x={centerX}
        y={centerY - scale * 1.1 - 8}
        textAnchor="middle"
        className="compass-label"
        fontSize="12"
        fill="#888"
      >
        N
      </text>
      <text
        x={centerX}
        y={centerY + scale * 1.1 + 16}
        textAnchor="middle"
        className="compass-label"
        fontSize="12"
        fill="#888"
      >
        S
      </text>
      <text
        x={centerX + scale * 1.1 + 8}
        y={centerY + 4}
        textAnchor="start"
        className="compass-label"
        fontSize="12"
        fill="#888"
      >
        E
      </text>
      <text
        x={centerX - scale * 1.1 - 8}
        y={centerY + 4}
        textAnchor="end"
        className="compass-label"
        fontSize="12"
        fill="#888"
      >
        W
      </text>

      {runways.map((runway, idx) => {
        const heading = runway.baseEnd.magneticHeading || 0;
        const length = runway.length || maxLength;
        const widthFt = runway.width || 100;
        const lengthScale = (length / maxLength) * scale;
        // Scale width proportionally to length, bounded for visibility
        const widthScale = Math.max(6, Math.min(18, (widthFt / length) * lengthScale * 3));

        // Runway is drawn centered at origin and rotated
        // Heading 0 = North (up), 90 = East (right)
        // SVG rotate: 0 = East, we need to adjust for heading
        const rotateAngle = heading - 90;

        const surfaceColor = getSurfaceColor(runway.surface);
        const isClosed = runway.closed;

        return (
          <g
            key={`${runway.identifier}-${idx}`}
            transform={`translate(${centerX}, ${centerY}) rotate(${rotateAngle})`}
            className="runway-group"
          >
            {/* Main runway body */}
            <rect
              x={-lengthScale / 2}
              y={-widthScale / 2}
              width={lengthScale}
              height={widthScale}
              fill={isClosed ? '#6B3232' : surfaceColor}
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="1"
              className="runway-body"
            />

            {/* Centerline stripes */}
            <line
              x1={-lengthScale / 2 + 4}
              x2={lengthScale / 2 - 4}
              y1={0}
              y2={0}
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1"
              strokeDasharray="4,4"
            />

            {/* Threshold markers */}
            <line
              x1={-lengthScale / 2}
              x2={-lengthScale / 2 + 3}
              y1={-widthScale / 2}
              y2={widthScale / 2}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2"
            />
            <line
              x1={lengthScale / 2 - 3}
              x2={lengthScale / 2}
              y1={-widthScale / 2}
              y2={widthScale / 2}
              stroke="rgba(255,255,255,0.9)"
              strokeWidth="2"
            />

            {/* Clickable hit area for base end */}
            {onRunwayClick && (
              <rect
                x={-lengthScale / 2}
                y={-widthScale / 2 - 6}
                width={lengthScale / 2}
                height={widthScale + 12}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => onRunwayClick(runway, runway.baseEnd.designator)}
              />
            )}
            {onRunwayClick && (
              <rect
                x={0}
                y={-widthScale / 2 - 6}
                width={lengthScale / 2}
                height={widthScale + 12}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => onRunwayClick(runway, runway.reciprocalEnd.designator)}
              />
            )}

            {/* Base end label (counter-rotate so text reads upright) */}
            <g transform={`translate(${-lengthScale / 2 - 14}, 0) rotate(${-rotateAngle})`}>
              <circle r="10" fill="rgba(0,0,0,0.7)" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="10"
                fontWeight="bold"
                fill="#fff"
              >
                {runway.baseEnd.designator}
              </text>
            </g>

            {/* Reciprocal end label */}
            <g transform={`translate(${lengthScale / 2 + 14}, 0) rotate(${-rotateAngle})`}>
              <circle r="10" fill="rgba(0,0,0,0.7)" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="10"
                fontWeight="bold"
                fill="#fff"
              >
                {runway.reciprocalEnd.designator}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
